import * as React from 'react';
import { useSelector } from 'react-redux';
import { MIN_SEARCH_INPUT_VALUE_LENGTH } from '../../../constants/searchInput';
import { selectIsSuggestionsError, selectSuggestionsLength } from '../../../selectors/searchModal';
import { selectSearchTagsLength } from '../../../selectors/tags';
import { ESearchModalState } from '../../../types/searchModal';

export interface IUseSearchModalStateParams {
  inputValueLength: number;
  isInputFocused: boolean;
}

interface IGetSearchModalStateParams extends IUseSearchModalStateParams {
  suggestionsLength: number;
  tagsLength: number;
  isSuggestionsError: boolean;
}

export const useSearchModalState = (
  params: IUseSearchModalStateParams,
): [ESearchModalState, React.Dispatch<React.SetStateAction<ESearchModalState>>] => {
  const [state, setState] = React.useState(ESearchModalState.EmptyTags);
  const { inputValueLength, isInputFocused } = params;
  const suggestionsLength = useSelector(selectSuggestionsLength);
  const tagsLength = useSelector(selectSearchTagsLength);
  const isSuggestionsError = useSelector(selectIsSuggestionsError);

  React.useEffect(() => {
    const newState = getSearchModalState({ ...params, suggestionsLength, tagsLength, isSuggestionsError });

    if (newState !== state) {
      setState(newState);
    }
  }, [isSuggestionsError, tagsLength, suggestionsLength, inputValueLength, isInputFocused]);

  return [state, setState];
};

function getSearchModalState(params: IGetSearchModalStateParams): ESearchModalState {
  const { inputValueLength, isInputFocused, isSuggestionsError, suggestionsLength, tagsLength } = params;

  if (isInputFocused && isSuggestionsError) {
    return ESearchModalState.SuggestError;
  } else if (isInputFocused && inputValueLength >= MIN_SEARCH_INPUT_VALUE_LENGTH) {
    return suggestionsLength ? ESearchModalState.Suggest : ESearchModalState.EmptySuggest;
  }

  return tagsLength ? ESearchModalState.Tags : ESearchModalState.EmptyTags;
}
